import React, { useState } from 'react';
import { Box, Chip, IconButton, Select, MenuItem, FormControl, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Composant pour afficher les tags d'un livre avec leurs couleurs et édition en ligne
 * @param {Object} props
 * @param {Array} props.tags - Array d'objets tags avec id, nom, couleur
 * @param {string} props.size - Taille des chips ('small', 'medium', 'large')
 * @param {Object} props.sx - Styles additionnels pour le conteneur
 * @param {Function} props.onTagClick - Callback optionnel lors du clic sur un tag
 * @param {Function} props.onTagRemove - Callback pour supprimer un tag (tag) => void
 * @param {Function} props.onTagAdd - Callback pour ajouter un tag (tagId) => void
 * @param {Array} props.availableTags - Liste des tags disponibles pour l'ajout
 * @param {boolean} props.editable - Si true, permet l'édition en ligne des tags
 * @param {boolean} props.isLoading - Si true, désactive les interactions pendant le chargement
 * @returns {JSX.Element}
 */
export function TagList({ 
  tags, 
  size = 'small', 
  sx = {}, 
  onTagClick, 
  onTagRemove, 
  onTagAdd, 
  availableTags = [], 
  editable = false,
  isLoading = false,
  forcePurple = false
}) {
  const { t } = useTranslation();
  const [showAddTag, setShowAddTag] = useState(false);

  if (!tags || (tags.length === 0 && !editable)) {
    return null;
  }

  // Filtrer les tags avec des IDs temporaires pour l'édition
  const validTags = tags?.filter(tag => tag && tag.id && !tag.id.toString().startsWith('temp-')) || [];
  const hasTemporaryTags = tags?.some(tag => tag && tag.id && tag.id.toString().startsWith('temp-'));

  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap', 
      gap: 0.5,
      alignItems: 'center',
      ...sx 
    }}>
      {tags?.map((tag, index) => {
        const isTemporary = tag.id && tag.id.toString().startsWith('temp-');
        return (
          <TagChip
            key={tag.id || index}
            tag={tag}
            size={size}
            onClick={onTagClick ? () => onTagClick(tag) : undefined}
            onRemove={editable && onTagRemove && !isTemporary ? () => onTagRemove(tag) : undefined}
            disabled={isLoading || isTemporary}
            forcePurple={forcePurple}
          />
        );
      })}
      
      {editable && onTagAdd && (
        <>
          {showAddTag ? (
            <AddTagInput
              availableTags={availableTags}
              existingTags={validTags} // Utiliser seulement les tags valides pour éviter les doublons
              onAdd={(tagId) => {
                onTagAdd(tagId);
                setShowAddTag(false);
              }}
              onCancel={() => setShowAddTag(false)}
              size={size}
              disabled={isLoading}
            />
          ) : (
            <IconButton
              size="small"
              onClick={() => setShowAddTag(true)}
              disabled={isLoading}
              title={hasTemporaryTags ? t('tagUtils.obsoleteFormatWarning') : t('tagUtils.addTag')}
              sx={{
                height: size === 'small' ? 24 : 28,
                minWidth: size === 'small' ? 24 : 28,
                padding: size === 'small' ? '0px 5px' : '6px 14px',
                border: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.1)',
                borderRadius: size === 'small' ? '6px' : '8px',
                backgroundColor: 'transparent',
                color: 'text.secondary',
                fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                '&:hover': {
                  borderColor: 'rgba(0, 0, 0, 0.3)',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  color: 'text.primary'
                }
              }}
            >
              <AddIcon fontSize={size === 'small' ? 'small' : 'medium'} />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
}

/**
 * Composant pour afficher un seul tag avec sa couleur
 * @param {Object} props
 * @param {Object} props.tag - Objet tag avec id, nom, couleur
 * @param {string} props.size - Taille du chip
 * @param {Function} props.onClick - Callback optionnel lors du clic
 * @param {Function} props.onRemove - Callback optionnel pour supprimer le tag
 * @param {boolean} props.disabled - Si true, désactive le chip
 * @returns {JSX.Element}
 */
// Fonction pour convertir hex en rgba
function hexToRgba(hex, alpha = 1) {
  const num = parseInt(hex.replace('#', ''), 16);
  const R = (num >> 16) & 255;
  const G = (num >> 8) & 255;
  const B = num & 255;
  return `rgba(${R}, ${G}, ${B}, ${alpha})`;
}

export function TagChip({ tag, size = 'small', onClick, onRemove, disabled = false, forcePurple = false }) {
  const { t } = useTranslation();
  const defaultColor = '#1976d2';
  // Utiliser violet si forcePurple est activé, sinon utiliser la couleur du tag
  const tagColor = forcePurple ? '#9c27b0' : (tag.couleur || defaultColor);
  const backgroundColor = hexToRgba(tagColor, 0.05); // Background avec transparence
  const isTemporary = tag.id && tag.id.toString().startsWith('temp-');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  const handleRemoveClick = () => {
    setShowConfirmDialog(true);
  };
  
  const handleConfirmRemove = () => {
    if (onRemove) {
      onRemove();
    }
    setShowConfirmDialog(false);
  };
  
  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
  };
  
  return (
    <>
    <Chip
      label={tag.nom}
      size={size}
      clickable={!!(onClick && !disabled)}
      onClick={onClick && !disabled ? onClick : undefined}
      onDelete={onRemove && !disabled ? handleRemoveClick : undefined}
      deleteIcon={onRemove ? <CloseIcon /> : undefined}
      disabled={disabled}
      title={isTemporary ? t('tagUtils.obsoleteFormatTooltip') : undefined}
      sx={{
        backgroundColor: isTemporary ? 'rgba(0, 0, 0, 0.1)' : backgroundColor,
        color: isTemporary ? 'text.secondary' : tagColor,
        fontSize: size === 'small' ? '12px' : '0.8125rem',
        fontWeight: 500,
        height: size === 'small' ? 24 : 28,
        padding: size === 'small' ? '4px 10px' : '6px 14px',
        borderRadius: size === 'small' ? '6px' : '8px',
        border: isTemporary ? '1px solid rgba(0, 0, 0, 0.2)' : `1px solid ${hexToRgba(tagColor, 0.15)}`,
        fontFamily: 'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        '& .MuiChip-deleteIcon': {
          color: isTemporary ? 'text.secondary' : tagColor,
          fontSize: size === 'small' ? '0.8rem' : '1rem',
          '&:hover': {
            color: isTemporary ? 'text.primary' : tagColor,
            opacity: 0.7,
          },
        },
        '&:hover': {
          backgroundColor: isTemporary ? 'rgba(0, 0, 0, 0.15)' : hexToRgba(tagColor, 0.25),
          opacity: (onClick || onRemove) && !disabled && !isTemporary ? 1 : 1,
        },
        // Pour les tags cliquables, ajouter un effet de curseur
        ...((onClick || onRemove) && !disabled && !isTemporary && {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: hexToRgba(tagColor, 0.25),
            opacity: 1,
            transform: 'scale(1.05)',
          }
        }),
        // Style pour les tags désactivés
        ...(disabled && {
          opacity: 0.6,
          cursor: 'default',
        })
      }}
    />
    
    {/* Boîte de dialogue de confirmation */}
    <Dialog
      open={showConfirmDialog}
      onClose={handleCancelRemove}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      <DialogTitle id="confirm-dialog-title">
        {t('tagUtils.confirmDeleteTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="confirm-dialog-description">
          {t('tagUtils.confirmDeleteMessage', { tagName: tag.nom })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancelRemove} color="primary">
          {t('tagUtils.cancel')}
        </Button>
        <Button onClick={handleConfirmRemove} color="error" variant="contained">
          {t('tagUtils.delete')}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

/**
 * Utilitaire pour convertir une chaîne de tags concaténés en array d'objets
 * Fallback pour les anciennes APIs qui renvoient encore des chaînes
 * @param {string} tagsString - Chaîne de tags séparés par des virgules
 * @returns {Array} Array d'objets tags avec des couleurs par défaut
 */
export function parseTagsString(tagsString) {
  if (!tagsString || typeof tagsString !== 'string') {
    return [];
  }
  
  return tagsString.split(', ').map((nom, index) => ({
    id: `temp-${index}`,
    nom: nom.trim(),
    couleur: '#007bff' // Couleur par défaut
  }));
}

/**
 * Utilitaire pour obtenir les tags avec couleurs à partir des données d'un livre
 * Gère à la fois le nouveau format (tags_with_colors) et l'ancien (tags string)
 * @param {Object} livre - Objet livre
 * @returns {Array} Array d'objets tags avec couleurs
 */
export function getLivreTagsWithColors(livre) {
  // Priorité au nouveau format
  if (livre.tags_with_colors && livre.tags_with_colors.length > 0) {
    return livre.tags_with_colors;
  }
  
  // Fallback vers l'ancien format
  if (livre.tags && typeof livre.tags === 'string') {
    return parseTagsString(livre.tags);
  }
  
  // Si tags est déjà un array d'objets (cas de getLivreById)
  if (Array.isArray(livre.tags)) {
    return livre.tags;
  }
  
  return [];
}

/**
 * Composant pour ajouter un tag avec autocomplete
 * @param {Object} props
 * @param {Array} props.availableTags - Liste des tags disponibles
 * @param {Array} props.existingTags - Tags déjà présents sur le livre
 * @param {Function} props.onAdd - Callback pour ajouter un tag (tagId) => void
 * @param {Function} props.onCancel - Callback pour annuler l'ajout
 * @param {string} props.size - Taille du composant
 * @param {boolean} props.disabled - Si true, désactive le composant
 * @returns {JSX.Element}
 */
function AddTagInput({ availableTags, existingTags = [], onAdd, onCancel, size = 'small', disabled = false }) {
  const { t } = useTranslation();
  const [selectedTag, setSelectedTag] = useState('');
  const [open, setOpen] = useState(true); // Ouvrir automatiquement le menu

  // Filtrer les tags déjà présents
  const existingTagIds = existingTags.map(tag => tag.id);
  const filteredTags = availableTags.filter(tag => !existingTagIds.includes(tag.id));

  // Ajouter automatiquement le tag dès qu'il est sélectionné
  const handleTagSelection = (event) => {
    const tagId = event.target.value;
    setSelectedTag(tagId);
    if (tagId) {
      // Ajouter immédiatement le tag sélectionné
      onAdd(tagId);
    }
  };

  // Fermer le menu quand on annule
  const handleCancel = () => {
    setOpen(false);
    onCancel();
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <FormControl size={size} sx={{ minWidth: 120 }}>
        <Select
          value={selectedTag}
          onChange={handleTagSelection}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          disabled={disabled}
          displayEmpty
          sx={{
            height: size === 'small' ? 24 : 32,
            fontSize: size === 'small' ? '0.75rem' : '1rem',
          }}
        >
          <MenuItem value="" disabled>
            <em>{t('tagUtils.selectTagPlaceholder')}...</em>
          </MenuItem>
          {filteredTags.map((tag) => (
            <MenuItem key={tag.id} value={tag.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: tag.couleur || '#007bff'
                  }}
                />
                {tag.nom}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        onClick={handleCancel}
        disabled={disabled}
        sx={{
          width: size === 'small' ? 20 : 28,
          height: size === 'small' ? 20 : 28,
          color: 'error.main',
          '&:hover': {
            backgroundColor: 'error.light',
          }
        }}
      >
        <CloseIcon fontSize={size === 'small' ? 'small' : 'medium'} />
      </IconButton>
    </Box>
  );
}
